import React, { createContext, useState, useCallback, useEffect, useMemo } from 'react'
import { Language, TranslationKey, TranslateParams, Translations } from './types'
import { nb } from './locales/nb'
import { en } from './locales/en'
import { installAcceptLanguageFetch } from './acceptLanguage'

// Ensure raw fetch() calls carry the active UI language (Accept-Language) for backend
// response localization. Installed once when the i18n module loads.
installAcceptLanguageFetch()

const DICTIONARIES: Record<Language, Translations> = { nb, en }
const STORAGE_KEY = 'structura.language'
const DEFAULT_LANGUAGE: Language = 'nb'

export interface I18nContextValue {
  language: Language
  setLanguage: (language: Language) => void
  t: (key: TranslationKey, params?: TranslateParams) => string
}

export const I18nContext = createContext<I18nContextValue | null>(null)

function isLanguage(value: string | null): value is Language {
  return value === 'nb' || value === 'en'
}

function interpolate(template: string, params?: TranslateParams): string {
  if (params === undefined) {
    return template
  }
  return template.replace(/\{(\w+)\}/g, (match: string, name: string): string => {
    const value = params[name]
    return value === undefined ? match : String(value)
  })
}

export interface I18nProviderProps {
  children: React.ReactNode
  defaultLanguage?: Language
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children, defaultLanguage = DEFAULT_LANGUAGE }) => {
  const [language, setLanguageState] = useState<Language>((): Language => {
    const stored = localStorage.getItem(STORAGE_KEY)
    return isLanguage(stored) ? stored : defaultLanguage
  })

  useEffect((): void => {
    document.documentElement.lang = language
  }, [language])

  const setLanguage = useCallback((next: Language): void => {
    localStorage.setItem(STORAGE_KEY, next)
    setLanguageState(next)
  }, [])

  const t = useCallback((key: TranslationKey, params?: TranslateParams): string => {
    const template = DICTIONARIES[language][key]
    if (template === undefined) {
      throw new Error(`Translation lookup failed: key "${key}" is not defined for language "${language}".`)
    }
    return interpolate(template, params)
  }, [language])

  const value = useMemo<I18nContextValue>((): I18nContextValue => ({ language, setLanguage, t }), [language, setLanguage, t])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}
